import React from 'react';
import { getRelativeDayLabel } from 'src/utils/relative-day-label';
import { DaySection } from './ChatMessage/day-section';

interface DayKeyed {
  timestamp: string;
}

interface DayGroup<T extends DayKeyed> {
  dayKey: string;
  dayLabel: string;
  items: T[];
}

function groupMessagesByDay<T extends DayKeyed>(messageList: T[]): DayGroup<T>[] {
  const dayGroups: DayGroup<T>[] = [];
  let currentGroup: DayGroup<T> | null = null;

  messageList.forEach((message) => {
    const relativeDayLabel = getRelativeDayLabel(message.timestamp);
    const dayKey = relativeDayLabel?.dayKey ?? message.timestamp.slice(0, 10);
    const dayLabel = relativeDayLabel?.label ?? dayKey;

    if (!currentGroup || currentGroup.dayKey !== dayKey) {
      if (currentGroup) {
        dayGroups.push(currentGroup);
      }
      currentGroup = { dayKey, dayLabel, items: [] };
    }

    currentGroup.items.push(message);
  });

  if (currentGroup) {
    dayGroups.push(currentGroup);
  }

  return dayGroups;
}

interface ChatDayGroupedMessagesProps<T extends DayKeyed> {
  messageList: T[];
  renderMessage: (message: T) => React.ReactNode;
}

export const ChatDayGroupedMessages = <T extends DayKeyed>({
  messageList,
  renderMessage,
}: ChatDayGroupedMessagesProps<T>): React.JSX.Element => {
  const dayGroups = groupMessagesByDay(messageList);

  return (
    <>
      {dayGroups.map((group) => (
        <DaySection showSeparator key={`day-${group.dayKey}`} label={group.dayLabel}>
          {group.items.map(renderMessage)}
        </DaySection>
      ))}
    </>
  );
};
